'use client';

import { useState } from 'react';
import type { DiagnosticResults } from './ResultsInteractive';

interface ActionPanelProps {
  results: DiagnosticResults;
  onContactRequest: () => void;
  onEmailReport: () => void;
  emailReportSent: boolean;
  emailReportError: string | null;
  emailReportLoading: boolean;
  onMeetingRequest: (periodoPreferencial: string, observacoes: string) => void;
  meetingRequestSent: boolean;
  meetingRequestError: string | null;
  meetingRequestLoading: boolean;
}

export default function ActionPanel({
  results,
  onContactRequest,
  onEmailReport,
  emailReportSent,
  emailReportError,
  emailReportLoading,
  onMeetingRequest,
  meetingRequestSent,
  meetingRequestError,
  meetingRequestLoading,
}: ActionPanelProps) {
  const [periodoPreferencial, setPeriodoPreferencial] = useState('');
  const [observacoes, setObservacoes] = useState('');

  const isUrgent = results.profile === 'support' || results.profile === 'pressure';

  const handleSubmitMeeting = () => {
    onMeetingRequest(periodoPreferencial, observacoes);
  };

  return (
    <div className="flex flex-col gap-4">
      {/* Urgency banner */}
      {isUrgent && (
        <div
          className="p-4"
          style={{ borderRadius: '5px', backgroundColor: 'rgba(198, 40, 40, 0.05)', border: '1px solid rgba(198, 40, 40, 0.2)' }}
        >
          <div className="flex items-start gap-2.5">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="#C62828" stroke="#C62828" strokeWidth="0" className="flex-shrink-0 mt-0.5" aria-hidden="true">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
            </svg>
            <div>
              <p
                className="text-xs font-semibold uppercase tracking-wide mb-1"
                style={{ fontFamily: 'DM Sans, sans-serif', letterSpacing: '0.08em', color: '#C62828' }}
              >
                Ação Recomendada
              </p>
              <p
                className="text-xs text-gray-700 leading-relaxed"
                style={{ fontFamily: 'DM Sans, sans-serif' }}
              >
                O seu diagnóstico indica necessidade de apoio técnico. Recomendamos agendar uma reunião de análise nos próximos dias.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Email report section */}
      <div
        className="bg-white border border-gray-100 p-5"
        style={{ borderRadius: '5px', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}
      >
        <div className="flex items-center gap-2 mb-3">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#009688" strokeWidth="2" aria-hidden="true">
            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
            <polyline points="22,6 12,13 2,6"/>
          </svg>
          <h4
            className="font-semibold text-sm text-gray-900"
            style={{ fontFamily: 'DM Sans, sans-serif' }}
          >
            Receber relatório por email
          </h4>
        </div>
        <p
          className="text-xs text-gray-500 mb-4 leading-relaxed"
          style={{ fontFamily: 'DM Sans, sans-serif' }}
        >
          Receba no endereço indicado um resumo estruturado do diagnóstico realizado, com o perfil identificado, áreas críticas e recomendações prioritárias.
        </p>

        {emailReportSent ? (
          <div
            className="flex items-start gap-2.5 p-3"
            style={{ borderRadius: '5px', backgroundColor: 'rgba(46, 125, 50, 0.05)', border: '1px solid rgba(46, 125, 50, 0.2)' }}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="#2E7D32" className="flex-shrink-0 mt-0.5" aria-hidden="true">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
            </svg>
            <p className="text-xs text-green-800 leading-relaxed" style={{ fontFamily: 'DM Sans, sans-serif' }}>
              O relatório foi enviado com sucesso para o endereço indicado.
            </p>
          </div>
        ) : (
          <>
            {emailReportError && (
              <div
                className="flex items-start gap-2.5 p-3 mb-3"
                style={{ borderRadius: '5px', backgroundColor: 'rgba(198, 40, 40, 0.05)', border: '1px solid rgba(198, 40, 40, 0.2)' }}
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="#C62828" className="flex-shrink-0 mt-0.5" aria-hidden="true">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
                </svg>
                <p className="text-xs leading-relaxed" style={{ fontFamily: 'DM Sans, sans-serif', color: '#C62828' }}>
                  Não foi possível enviar o relatório para o endereço indicado. Por favor, tente novamente dentro de instantes.
                </p>
              </div>
            )}
            <button
              type="button"
              onClick={onEmailReport}
              disabled={emailReportLoading}
              className="w-full h-10 font-semibold text-sm transition-all duration-250 flex items-center justify-center gap-2"
              style={{
                fontFamily: 'DM Sans, sans-serif',
                borderRadius: '5px',
                backgroundColor: emailReportLoading ? '#F5F5F5' : '#009688',
                color: emailReportLoading ? '#999' : 'white',
                border: emailReportLoading ? '2px solid transparent' : '2px solid #009688',
                cursor: emailReportLoading ? 'not-allowed' : 'pointer',
              }}
            >
              {emailReportLoading ? (
                <>
                  <div
                    className="w-4 h-4 border-2 border-t-transparent rounded-full animate-spin"
                    style={{ borderColor: '#999', borderTopColor: 'transparent' }}
                  />
                  A enviar...
                </>
              ) : (
                <>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                    <polyline points="22,6 12,13 2,6"/>
                  </svg>
                  Receber Relatório por Email
                </>
              )}
            </button>
          </>
        )}
      </div>

      {/* Meeting request section — institutional CTA */}
      <div
        className="bg-white border border-gray-100 p-5"
        style={{ borderRadius: '5px', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}
      >
        <div className="flex items-center gap-2 mb-3">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#009688" strokeWidth="2" aria-hidden="true">
            <rect x="3" y="4" width="18" height="18" rx="2"/>
            <line x1="16" y1="2" x2="16" y2="6"/>
            <line x1="8" y1="2" x2="8" y2="6"/>
            <line x1="3" y1="10" x2="21" y2="10"/>
          </svg>
          <h4
            className="font-semibold text-sm text-gray-900"
            style={{ fontFamily: 'DM Sans, sans-serif' }}
          >
            Agendar reunião de análise
          </h4>
        </div>

        {meetingRequestSent ? (
          <div
            className="flex items-start gap-2.5 p-3"
            style={{ borderRadius: '5px', backgroundColor: 'rgba(46, 125, 50, 0.05)', border: '1px solid rgba(46, 125, 50, 0.2)' }}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="#2E7D32" className="flex-shrink-0 mt-0.5" aria-hidden="true">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
            </svg>
            <p className="text-xs text-green-800 leading-relaxed" style={{ fontFamily: 'DM Sans, sans-serif' }}>
              O seu pedido de agendamento foi recebido. A equipa GestPub entrará em contacto brevemente.
            </p>
          </div>
        ) : (
          <>
            <p
              className="text-xs text-gray-500 mb-4 leading-relaxed"
              style={{ fontFamily: 'DM Sans, sans-serif' }}
            >
              Se considerar útil, a GestPub poderá analisar consigo os resultados do diagnóstico e identificar prioridades de intervenção ajustadas à realidade da sua Junta de Freguesia.
            </p>

            {meetingRequestError && (
              <div
                className="flex items-start gap-2.5 p-3 mb-3"
                style={{ borderRadius: '5px', backgroundColor: 'rgba(198, 40, 40, 0.05)', border: '1px solid rgba(198, 40, 40, 0.2)' }}
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="#C62828" className="flex-shrink-0 mt-0.5" aria-hidden="true">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
                </svg>
                <p className="text-xs leading-relaxed" style={{ fontFamily: 'DM Sans, sans-serif', color: '#C62828' }}>
                  Não foi possível enviar o pedido de agendamento. Por favor, tente novamente dentro de instantes.
                </p>
              </div>
            )}

            {/* Optional: preferred contact period */}
            <div className="mb-3">
              <label
                className="block text-xs font-medium text-gray-600 mb-1.5"
                style={{ fontFamily: 'DM Sans, sans-serif' }}
              >
                Período preferencial para contacto{' '}
                <span className="text-gray-400 font-normal">(opcional)</span>
              </label>
              <select
                value={periodoPreferencial}
                onChange={(e) => setPeriodoPreferencial(e.target.value)}
                className="w-full h-9 px-3 text-xs text-gray-700 bg-white border border-gray-200 focus:outline-none focus:border-teal-500 transition-colors"
                style={{ borderRadius: '5px', fontFamily: 'DM Sans, sans-serif' }}
              >
                <option value="">Sem preferência</option>
                <option value="Manhã">Manhã</option>
                <option value="Tarde">Tarde</option>
                <option value="Final do dia">Final do dia</option>
              </select>
            </div>

            {/* Optional: observations */}
            <div className="mb-4">
              <label
                className="block text-xs font-medium text-gray-600 mb-1.5"
                style={{ fontFamily: 'DM Sans, sans-serif' }}
              >
                Observações{' '}
                <span className="text-gray-400 font-normal">(opcional)</span>
              </label>
              <textarea
                value={observacoes}
                onChange={(e) => setObservacoes(e.target.value)}
                rows={2}
                placeholder="Indique qualquer informação adicional relevante..."
                className="w-full px-3 py-2 text-xs text-gray-700 bg-white border border-gray-200 focus:outline-none focus:border-teal-500 transition-colors resize-none"
                style={{ borderRadius: '5px', fontFamily: 'DM Sans, sans-serif' }}
              />
            </div>

            <button
              type="button"
              onClick={handleSubmitMeeting}
              disabled={meetingRequestLoading}
              className="w-full h-10 font-semibold text-sm transition-all duration-250 flex items-center justify-center gap-2"
              style={{
                fontFamily: 'DM Sans, sans-serif',
                borderRadius: '5px',
                backgroundColor: meetingRequestLoading ? '#F5F5F5' : '#009688',
                color: meetingRequestLoading ? '#999' : 'white',
                border: meetingRequestLoading ? '2px solid transparent' : '2px solid #009688',
                cursor: meetingRequestLoading ? 'not-allowed' : 'pointer',
              }}
            >
              {meetingRequestLoading ? (
                <>
                  <div
                    className="w-4 h-4 border-2 border-t-transparent rounded-full animate-spin"
                    style={{ borderColor: '#999', borderTopColor: 'transparent' }}
                  />
                  A enviar...
                </>
              ) : (
                <>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                    <rect x="3" y="4" width="18" height="18" rx="2"/>
                    <line x1="16" y1="2" x2="16" y2="6"/>
                    <line x1="8" y1="2" x2="8" y2="6"/>
                    <line x1="3" y1="10" x2="21" y2="10"/>
                  </svg>
                  Solicitar agendamento
                </>
              )}
            </button>
          </>
        )}
      </div>

      {/* Contact request */}
      <div
        className="bg-white border border-gray-100 p-5"
        style={{ borderRadius: '5px', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}
      >
        <div className="flex items-center gap-2 mb-3">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#009688" strokeWidth="2" aria-hidden="true">
            <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 014.69 13a19.79 19.79 0 01-3.07-8.67A2 2 0 013.6 2.18h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L7.91 9.91a16 16 0 006.06 6.06l1.79-1.79a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/>
          </svg>
          <h4
            className="font-semibold text-sm text-gray-900"
            style={{ fontFamily: 'DM Sans, sans-serif' }}
          >
            Pedido de Contacto
          </h4>
        </div>
        <p
          className="text-xs text-gray-500 mb-4 leading-relaxed"
          style={{ fontFamily: 'DM Sans, sans-serif' }}
        >
          Prefere que entremos em contacto consigo? Submeta um pedido e a nossa equipa responderá em 24 horas úteis.
        </p>
        <button
          type="button"
          onClick={onContactRequest}
          className="w-full h-10 font-semibold text-sm transition-all duration-250 flex items-center justify-center gap-2 active:scale-98 hover:bg-[#F0FAFA]"
          style={{
            fontFamily: 'DM Sans, sans-serif',
            borderRadius: '5px',
            border: '2px solid #009688',
            color: '#009688',
            backgroundColor: 'transparent',
          }}
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>
          </svg>
          Solicitar Contacto
        </button>
      </div>
    </div>
  );
}